import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, MapPin, Trash2, Save, Move } from 'lucide-react';

const PLOT_STATUSES = [
  { value: 'available', label: 'Available', color: 'bg-green-500' },
  { value: 'reserved', label: 'Reserved', color: 'bg-yellow-500' },
  { value: 'sold', label: 'Sold', color: 'bg-red-500' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-orange-500' },
];

const colorFor = (s: string) => PLOT_STATUSES.find(p => p.value === s)?.color || 'bg-gray-500';

interface Props {
  projectId: string;
}

export function PlotMapEditor({ projectId }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [editPlot, setEditPlot] = useState<any>(null);

  const { data: plots = [] } = useQuery({
    queryKey: ['project-plots', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_plots' as any)
        .select('*, bookings(customer_name, customer_phone)')
        .eq('project_id', projectId)
        .order('created_at');
      if (error) throw error;
      return data as any[];
    },
    enabled: !!projectId,
  });

  const { data: bookings = [] } = useQuery({
    queryKey: ['bookings-for-plot-link', projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id, customer_name, plot_number')
        .eq('project_id', projectId);
      return data || [];
    },
    enabled: !!projectId,
  });

  const addPlot = useMutation({
    mutationFn: async () => {
      const nextNum = (plots.length || 0) + 1;
      const { error } = await supabase.from('project_plots' as any).insert({
        project_id: projectId,
        label: `Plot ${nextNum}`,
        x_percent: 50,
        y_percent: 50,
        status: 'available',
        created_by: user!.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
      toast.success('Plot added');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updatePlot = useMutation({
    mutationFn: async (plot: any) => {
      const { id, bookings: _b, ...rest } = plot;
      const { error } = await supabase.from('project_plots' as any).update(rest).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-plots', projectId] }),
    onError: (e: any) => toast.error(e.message),
  });

  const deletePlot = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('project_plots' as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
      toast.success('Plot deleted');
    },
  });

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingId || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    qc.setQueryData(['project-plots', projectId], (old: any[] = []) =>
      old.map(p => p.id === draggingId ? { ...p, x_percent: x, y_percent: y } : p)
    );
  };

  const onPointerUp = () => {
    if (draggingId) {
      const plot = plots.find(p => p.id === draggingId);
      if (plot) updatePlot.mutate({ id: plot.id, x_percent: plot.x_percent, y_percent: plot.y_percent });
    }
    setDraggingId(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={editMode ? 'default' : 'outline'}
            onClick={() => setEditMode(v => !v)}
            className="gap-1"
          >
            <Move className="h-4 w-4" /> {editMode ? 'Editing — Click to exit' : 'Edit Map'}
          </Button>
          {editMode && (
            <Button size="sm" variant="outline" onClick={() => addPlot.mutate()} className="gap-1">
              <Plus className="h-4 w-4" /> Add Plot
            </Button>
          )}
        </div>
        <div className="flex gap-3 text-xs">
          {PLOT_STATUSES.map(s => (
            <div key={s.value} className="flex items-center gap-1">
              <div className={`h-3 w-3 rounded-full ${s.color}`} />
              <span className="text-muted-foreground">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div
        ref={containerRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        className="relative w-full aspect-[16/9] rounded-2xl border-2 border-dashed bg-gradient-to-br from-emerald-50 to-blue-50 overflow-hidden select-none"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 40px)' }}
      >
        {plots.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {editMode ? 'Click "Add Plot" to place markers on the map.' : 'No plots placed yet.'}
          </div>
        )}
        {plots.map((p: any) => (
          <div
            key={p.id}
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: `${p.x_percent}%`, top: `${p.y_percent}%` }}
          >
            <button
              onPointerDown={(e) => {
                if (editMode) {
                  e.preventDefault();
                  setDraggingId(p.id);
                }
              }}
              onClick={() => !draggingId && setEditPlot(p)}
              className={`group flex flex-col items-center ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
            >
              <div className="px-2 py-0.5 mb-0.5 rounded bg-white shadow text-[10px] font-bold border whitespace-nowrap">
                {p.label}
              </div>
              <MapPin className={`h-7 w-7 ${colorFor(p.status).replace('bg-', 'text-')} drop-shadow-md`} fill="currentColor" />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!editPlot} onOpenChange={(o) => !o && setEditPlot(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Plot</DialogTitle></DialogHeader>
          {editPlot && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Label *</Label>
                <Input
                  className="h-9 text-sm"
                  value={editPlot.label}
                  onChange={e => setEditPlot({ ...editPlot, label: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">X (%)</Label>
                  <Input type="number" min={0} max={100} step="0.1" className="h-9 text-sm"
                    value={editPlot.x_percent}
                    onChange={e => setEditPlot({ ...editPlot, x_percent: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-xs">Y (%)</Label>
                  <Input type="number" min={0} max={100} step="0.1" className="h-9 text-sm"
                    value={editPlot.y_percent}
                    onChange={e => setEditPlot({ ...editPlot, y_percent: Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editPlot.status} onValueChange={v => setEditPlot({ ...editPlot, status: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLOT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Linked Booking</Label>
                <Select
                  value={editPlot.booking_id || 'none'}
                  onValueChange={v => setEditPlot({ ...editPlot, booking_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {bookings.map((b: any) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.customer_name} {b.plot_number ? `— Plot ${b.plot_number}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} className="text-sm"
                  value={editPlot.notes || ''}
                  onChange={e => setEditPlot({ ...editPlot, notes: e.target.value })} />
              </div>
              {editPlot.bookings && (
                <div className="rounded border p-2 text-xs bg-muted/40">
                  <Badge variant="secondary" className="text-[10px] mb-1">Current booking</Badge>
                  <div><strong>{editPlot.bookings.customer_name}</strong> — {editPlot.bookings.customer_phone}</div>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  className="flex-1 gap-1"
                  onClick={() => {
                    updatePlot.mutate(editPlot, {
                      onSuccess: () => { toast.success('Plot updated'); setEditPlot(null); }
                    });
                  }}
                  disabled={updatePlot.isPending}
                >
                  <Save className="h-4 w-4" /> Save
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => {
                    if (confirm('Delete this plot?')) {
                      deletePlot.mutate(editPlot.id);
                      setEditPlot(null);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
